import { useState, type ReactNode } from 'react';
import type {
  AnswerDecisionBody,
  ClaimAdjudicationPayload,
  DecisionSummary,
  RunDetail,
} from '@ni/shared';
import { useOrg } from '../../api/app-context';
import { useAnswerDecision, useDecision } from '../../api/hooks';
import { standingAnswerLine, standingAnswerOf, loadErrorLine } from '../../api/errors';
import { FACT_TYPE_LABELS, KEPT_JUST_NOW, sourceTierLabel } from '../../lib/copy';
import { factLine, type FactReader } from '@ni/shared';
import { formatDate, joinClauses } from '../../lib/format';
import { ErrorLine } from '../ui/error-line';
import { WorkingButton } from '../ui/working-button';
import { Loading } from '../ui/loading';
import './finding-review.css';

/**
 * The finding review under the run strip (ui-design §4.4). The run stopped
 * **before summary generation** so the narrative never carries facts the office
 * has not passed on — the copy says held, not broken.
 *
 * It began as the blocking-only panel and widened on 2026-08-31 (§3f): a run
 * now parks before its summary on **every** open question, so every open
 * question is asked here, in one place, while the rest of the page shows the
 * dossier as it stood before this run. The two kinds differ only in what they
 * are asking about, and the copy follows:
 *
 *  - a **blocking** row withheld its fact until it is answered, so "Keep in
 *    brief" is a decision to put something on the page, and "Wrong person" is
 *    live because a withheld severe fact is exactly where a misbind surfaces;
 *  - a **queued** row's fact already landed and is already rendering at its
 *    place in the brief, so it is the plainer keep/leave-out call the inline
 *    row makes (fact-row.tsx) — same endpoint, same words as inline, so the two
 *    surfaces never read as two different questions.
 *
 * "No longer true" is deliberately absent from both: it exists only as a
 * fact-level correction, and offering it on an adjudication row would be a
 * contract violation, not a wording slip.
 */

type Action = 'keep' | 'leave_out' | 'wrong_person';

/**
 * **What the card puts in front of the office to answer from.**
 *
 * The excerpt, where there is one — the source's own sentence is the best thing
 * this question can show. There is not always one: the extractor may return a
 * claim with neither `raw` nor an evidence excerpt (a structured source with
 * nothing quotable in it), ingest stores that as null, and until 2026-09-10 the
 * read coerced it to `''` and this card rendered an empty paragraph over two
 * buttons — a question with nothing in it.
 *
 * So the same ladder the Flagged findings section walks (`flagged-findings.tsx`,
 * `statement`), in this card's order: the excerpt first, then the finding's own
 * sentence written from the claim's `fields`, then the type label. The order
 * differs from that section's on purpose — there the lineage's *current* version
 * outranks the excerpt the question was raised on, and here there is no lineage
 * to be more current than anything.
 *
 * `fields` is checked for emptiness rather than handed straight to `factLine`,
 * because `factLine`'s own last resort on an empty payload is "No readable
 * detail", and a type label says strictly more than that.
 */
export function findingLine(
  payload: Pick<ClaimAdjudicationPayload, 'evidence_excerpt' | 'fact_type' | 'fields'>,
  reader: FactReader,
): string {
  /* **Trimmed, and whitespace is not an excerpt** (2026-09-10, found at review).
     The read normalizes the same way (`decision-read.service.ts`,
     `asStringOrNull`), and this rung is written to hold on its own regardless:
     a truthiness test alone let `"\n "` through as a sentence, which is the
     blank paragraph the fallback exists to remove, reached by a payload one
     whitespace character away from the one that was fixed. Two guards for one
     rule is deliberate — this ladder is what the card renders, and it must not
     depend on a projection elsewhere having been careful. */
  const excerpt = payload.evidence_excerpt?.trim();
  if (excerpt) return excerpt;
  if (Object.keys(payload.fields).length > 0) {
    return factLine({ fact_type: payload.fact_type, fields: payload.fields }, reader);
  }
  return FACT_TYPE_LABELS[payload.fact_type];
}

/**
 * **The paragraph the office answers from**, as its own component so the wiring
 * is testable and not merely typed.
 *
 * The reader is this page's subject by **id** and by nothing else. The card only
 * ever renders on that subject's own dossier, and `factLine` turns on the id
 * everywhere it can — the name is its last-resort probe for a ref carrying no id
 * at all, and guessing from a spelling is the render the ids were brought in to
 * end (`fact-copy.ts`).
 *
 * Split out at review of the empty-card fix: the ladder above was pinned, but
 * the `<p>` that was printing `payload.evidence_excerpt` raw was reachable by no
 * test at all, which is the half that actually shipped the blank card. This is
 * the smallest boundary that puts it under one (`finding-review.spec.tsx`).
 */
export function FindingParagraph({
  payload,
  subjectId,
}: {
  payload: Pick<ClaimAdjudicationPayload, 'evidence_excerpt' | 'fact_type' | 'fields'>;
  subjectId: string;
}) {
  return <p>{findingLine(payload, { id: subjectId, name: '' })}</p>;
}

/** Blocking wording; a queued fact is already on the brief, so it reads plainer. */
const SETTLED_LINE: Record<Action, string> = {
  keep: KEPT_JUST_NOW,
  leave_out: 'Left out · just now',
  wrong_person: 'Marked wrong person · just now',
};

const QUEUED_SETTLED_LINE: Partial<Record<Action, string>> = {
  keep: KEPT_JUST_NOW,
  leave_out: 'Kept off the brief · just now',
};

const ANSWER_BODY: Record<Action, AnswerDecisionBody> = {
  keep: { keep: true },
  leave_out: { leave_out: true },
  wrong_person: { wrong_person: true },
};

function AdjudicationCard({
  subjectId,
  decisionId,
  blocking,
  runId,
}: {
  subjectId: string;
  decisionId: string;
  /**
   * From the record's pending row rather than the decision read, so the card
   * knows which question it is asking before its own fetch resolves. `null`
   * cannot occur for `claim_adjudication` (the contract only nulls it on the
   * identity kinds); it is treated as queued, the weaker of the two claims.
   */
  blocking: boolean | null;
  runId: string;
}) {
  const decision = useDecision(decisionId);
  const answer = useAnswerDecision(subjectId);
  /* The first-party tier reads in the org's own words. */
  const { vertical } = useOrg();
  const [clicked, setClicked] = useState<Action | null>(null);
  const [settled, setSettled] = useState<Action | null>(null);

  // A queued row's fact is already on the brief, so "held" language does not
  // fit it; it borrows the inline row's words instead.
  const held = blocking === true;

  const standing = standingAnswerOf(answer.error);
  const loaded =
    decision.data && decision.data.kind === 'claim_adjudication' ? decision.data.payload : null;
  const label = loaded ? FACT_TYPE_LABELS[loaded.fact_type] : null;
  if (standing) {
    // The two-user case, handled as information: someone answered first, so the
    // standing answer renders in place and the refetch is already in flight.
    return <SettledRow label={label} line={standingAnswerLine(standing)} />;
  }
  if (settled) {
    /* The `??` covers only `wrong_person`, which no queued card offers — the
       blocking wording is the honest thing to fall back to if one ever does. */
    const line = (held ? undefined : QUEUED_SETTLED_LINE[settled]) ?? SETTLED_LINE[settled];
    return <SettledRow label={label} line={line} />;
  }

  if (decision.isPending) return <SettledRow label={null} line={<Loading />} />;
  if (decision.isError) return <SettledRow label={null} line={loadErrorLine(decision.error)} />;
  if (decision.data.kind !== 'claim_adjudication') return null;
  if (decision.data.status !== 'pending') return null;

  const payload = decision.data.payload;

  const act = (action: Action) => {
    setClicked(action);
    answer.mutate(
      { decisionId, body: ANSWER_BODY[action], runId },
      {
        onSuccess: () => setSettled(action),
        onSettled: () => setClicked(null),
      },
    );
  };

  const busy = clicked !== null;

  return (
    <div className="adj-card rv">
      <div className="k">
        {FACT_TYPE_LABELS[payload.fact_type]}
        <small>{sensitivityLabel(payload.adjudication_sensitivity)}</small>
      </div>

      <div>
        <FindingParagraph payload={payload} subjectId={subjectId} />

        <div className="attribution">
          <span className="sc lead-word">Attributed via</span> {payload.subject_binding}
        </div>

        <div className="src">
          {joinClauses([
            sourceTierLabel(vertical, payload.source_tier),
            payload.content_date ? formatDate(payload.content_date) : null,
          ])}
        </div>

        <div className="rv-acts">
          <WorkingButton
            label={held ? 'Keep in brief' : 'Keep'}
            workingLabel="Keeping…"
            variant="act"
            working={clicked === 'keep'}
            disabled={busy && clicked !== 'keep'}
            onClick={() => act('keep')}
          />
          <WorkingButton
            label="Leave out"
            workingLabel="Leaving out…"
            variant="act"
            working={clicked === 'leave_out'}
            disabled={busy && clicked !== 'leave_out'}
            onClick={() => act('leave_out')}
          />
          {/*
            Wrong person is a blocking-row gesture only, matching the inline queued
            row, which has never offered it either. A withheld severe fact is the
            place a misbind shows itself, and answering there unwinds the whole
            identity; a queued finding that already landed is a keep-or-leave call,
            and the recourse for "this is not him" is the identity line's own
            Wrong person, which says what it will do before it does it.
          */}
          {held ? (
            <WorkingButton
              label="Wrong person"
              workingLabel="Removing…"
              variant="act"
              working={clicked === 'wrong_person'}
              disabled={busy && clicked !== 'wrong_person'}
              onClick={() => act('wrong_person')}
            />
          ) : null}
        </div>

        {answer.isError ? <ErrorLine error={answer.error} /> : null}
      </div>
    </div>
  );
}

/** "Medium sensitivity", from the payload's level word. */
function sensitivityLabel(level: string): string {
  return `${level.charAt(0).toUpperCase()}${level.slice(1)} sensitivity`;
}

/** An answered row stays in place, settled to one line under its label. */
function SettledRow({ label, line }: { label: string | null; line: ReactNode }) {
  return (
    <div className="adj-settled rv settled">
      <div className="k">{label}</div>
      <div className="done-line">{line}</div>
    </div>
  );
}

export function FindingReview({
  subjectId,
  run,
  decisions,
}: {
  subjectId: string;
  run: RunDetail;
  /**
   * Every pending `claim_adjudication` on this run — the caller has already
   * scoped by `run_id`. Order is the record read's (`created_at` ascending), so
   * the questions come in the order the run raised them; blocking rows are not
   * floated to the top, because the office answers a list, not a ranking.
   */
  decisions: DecisionSummary[];
}) {
  // Counts every adjudication on the run since 2026-08-31 (§3f) — the same set
  // the cards below list and the same set the continuation waits on, so
  // "2 of 5 reviewed" and the strip's count can never disagree.
  const counter = run.adjudications;

  return (
    <div className="finding-review">
      <div className="review-head">
        <div className="lead-line">The brief is on hold until these are reviewed.</div>
        {counter && counter.total > 0 ? (
          <div className="counter">
            {counter.answered} of {counter.total} reviewed
          </div>
        ) : null}
      </div>

      {decisions.map((decision) => (
        <AdjudicationCard
          key={decision.id}
          subjectId={subjectId}
          decisionId={decision.id}
          blocking={decision.blocking}
          runId={run.id}
        />
      ))}
    </div>
  );
}
